// lineageLayouts.js — the geometry behind the Lineage tab's alternate views.
//
// Pure and DOM-free, the same split `flowGeometry` and `termArch` already use:
// these functions take a built provenance graph and return coordinates, and the
// components map coordinates to elements. That is what makes a layout testable
// at all — a layout computed inside a render pass can only be checked by looking
// at it, and "looks about right" is how the bipartite view kept its defects.
//
// Every layout here exists because the shipped flow view cannot answer something:
//
//   arc      it is bipartite, so a CHAIN (read → write → PR) is undrawable —
//            three nodes across two columns lose the middle.
//   dome     94 edges between 6 families is unreadable per-node and obvious
//            per-family; nothing else says "research never feeds code".
//   lanes    provenance is WITHIN a session, and nothing shows that the window's
//            lanes are three minutes long, which is why the graph is thin.
//   matrix   an absent tier is invisible in a node-link view. Zero citations
//            should be a blank column, not a number nobody reads.
//   depth    "how far back does the evidence go" is the question the two-column
//            layout structurally cannot ask, because it always has two columns.
//
// Shared conventions:
//   • Nodes are ordered by (ts, id) so a layout is deterministic on equal
//     timestamps — the scan hydrates concurrently, so ties are routine.
//   • Unlinked nodes are first-class. Every layout that can show them does.
//   • Nothing here reads `window`, measures the DOM, or rounds for pixels.

const TIERS = ["cited", "quoted", "entity", "adjacent"];

/** Family of a node kind — "file.read" and "file.write" are one family. */
export const familyOf = (kind) => String(kind || "").split(".")[0] || "other";

const tsOf = (v) => {
  const t = typeof v === "number" ? v : Date.parse(v);
  return Number.isFinite(t) ? t : 0;
};

/** Deterministic node order: time, then id to break the ties concurrency creates. */
export function orderedNodes(built) {
  return [...(built?.nodes || [])].sort((a, b) => (tsOf(a.ts) - tsOf(b.ts)) || String(a.id).localeCompare(String(b.id)));
}

/** Ids that reached at least one edge. The complement is the unlinked half. */
export function linkedIds(built) {
  const s = new Set();
  for (const e of built?.edges || []) { s.add(e.source.id); s.add(e.target.id); }
  return s;
}

// ── 1. ARC ───────────────────────────────────────────────────────────────────

/**
 * One time axis; derivation arcs above it, consultation below.
 *
 * Arc height grows with span, so a long-range dependency reads as tall — and a
 * window whose arcs are all short is one with no real chains, which is a
 * finding the bipartite view cannot render.
 *
 * Unlinked nodes stay ON the axis as ticks. They are most of a real window, and
 * leaving them out is what makes a sparse graph look complete.
 */
export function arcLayout(built, { width = 900, height = 260, pad = 26, maxArc = 105 } = {}) {
  const nodes = orderedNodes(built);
  const linked = linkedIds(built);
  const axisY = Math.round(height * 0.62);
  const inner = Math.max(1, width - pad * 2);
  const step = nodes.length > 1 ? inner / (nodes.length - 1) : 0;
  const xOf = new Map();
  const placed = nodes.map((n, i) => {
    const x = pad + i * step;
    xOf.set(n.id, x);
    return { ...n, x, y: axisY, linked: linked.has(n.id), family: familyOf(n.kind) };
  });
  const arcs = [];
  for (const e of built?.edges || []) {
    const x1 = xOf.get(e.source.id);
    const x2 = xOf.get(e.target.id);
    // An edge whose endpoints are not both in `nodes` cannot be placed. Skip it
    // rather than drawing to NaN, which silently blanks the whole <path>.
    if (x1 == null || x2 == null) continue;
    const up = e.class !== "consultation";
    const h = Math.min(maxArc, 26 + Math.abs(x2 - x1) * 0.3);
    const cy = axisY + (up ? -h : h);
    arcs.push({
      id: `${e.source.id}→${e.target.id}:${e.tier}`,
      d: `M${x1},${axisY} C${x1},${cy} ${x2},${cy} ${x2},${axisY}`,
      tier: e.tier, cls: e.class, up, span: Math.abs(x2 - x1), edge: e,
    });
  }
  // Tall arcs first so short ones draw over them rather than under.
  arcs.sort((a, b) => b.span - a.span);
  return { axisY, width, height, nodes: placed, arcs, unlinked: placed.filter((n) => !n.linked).length };
}

// ── 2. ARCH ──────────────────────────────────────────────────────────────────

/**
 * The provenance graph in the shape `termArch.buildArch` already consumes, so
 * the panel gets the Terms arch — seriation, sub-span anchoring, the frame — for
 * free instead of reimplementing a worse version of each.
 *
 *   Terms:   segment = a term,     span = prompts,        ribbon = shared prompts
 *   Lineage: segment = an ARTIFACT, span = ops touching it, ribbon = provenance edges
 *
 * Segments are ARTIFACTS, not families. Aggregating to families was the mistake
 * the first version made and it is degenerate by construction: a real window is
 * ~80% files, so one segment eats three quarters of the arch, the rest are
 * slivers, and the dominant ribbon is file→file — a self-loop that renders as a
 * blob. Terms reads well because it has ~40 comparable segments with many-to-many
 * links, and that is exactly what artifacts give.
 *
 * Ranked by degree then volume, because a segment that links to nothing costs
 * arch length and teaches nothing; the caller reports what was dropped.
 */
export function archInput(built, { limit = 40 } = {}) {
  // A segment must be a THING, so it needs a REF. Falling back to the label was
  // the defect: a lens that declares `ref: () => null` is stating that its op
  // names no durable target — `sections` says exactly that, because a scripted
  // program has none — and its label is a sentence ("asked 2 of grep at once").
  // Those became 26 of 34 segments on a real window, each captioned with prose,
  // which is what made the arch look like noise. They are counted, not drawn.
  const identityOf = (n) => `${familyOf(n.kind)}:${n.ref}`;
  const meta = new Map();
  let unnamed = 0;
  for (const n of orderedNodes(built)) {
    if (!n.ref) { unnamed += 1; continue; }
    const id = identityOf(n);
    const cur = meta.get(id) || { term: id, count: 0, degree: 0, family: familyOf(n.kind), node: n };
    cur.count += 1;
    meta.set(id, cur);
  }
  // Pairs between DISTINCT artifacts. A read and a write of one file collapse to
  // the same identity, and a self-ribbon is a blob rather than a relationship.
  const pair = new Map();
  for (const e of built?.edges || []) {
    if (!e.source.ref || !e.target.ref) continue;
    const a = identityOf(e.source);
    const b = identityOf(e.target);
    if (a === b) continue;
    const k = a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`;
    const cur = pair.get(k) || { a: a < b ? a : b, b: a < b ? b : a, weight: 0, tier: e.tier };
    cur.weight += 1;
    if (TIERS.indexOf(e.tier) < TIERS.indexOf(cur.tier)) cur.tier = e.tier;
    pair.set(k, cur);
  }
  for (const p of pair.values()) {
    if (meta.has(p.a)) meta.get(p.a).degree += p.weight;
    if (meta.has(p.b)) meta.get(p.b).degree += p.weight;
  }
  const ranked = [...meta.values()].sort((x, y) => (y.degree - x.degree) || (y.count - x.count) || x.term.localeCompare(y.term));
  const items = ranked.slice(0, limit);
  const kept = new Set(items.map((i) => i.term));
  const edges = [...pair.values()].filter((p) => kept.has(p.a) && kept.has(p.b));
  return { items, edges, total: ranked.length, shown: items.length, unnamed, unlinked: ranked.filter((r) => !r.degree).length };
}

// ── 3. LANES ─────────────────────────────────────────────────────────────────

/**
 * Time across, session down — and edges only ever inside a lane, because that
 * is the scope rule. A window of short lanes cannot produce chains, and this is
 * the only view where that reads as a property of the sample rather than as an
 * empty panel.
 */
export function laneLayout(built, { width = 900, laneH = 52, pad = 130, top = 34, maxArc = 22 } = {}) {
  const nodes = orderedNodes(built);
  const linked = linkedIds(built);
  const bySession = new Map();
  for (const n of nodes) {
    const k = n.sessionId || "";
    if (!bySession.has(k)) bySession.set(k, []);
    bySession.get(k).push(n);
  }
  // Busiest lane first: a reader scanning from the top should meet the session
  // that actually has a story before the ones with two nodes.
  const order = [...bySession.entries()].sort((a, b) => (b[1].length - a[1].length) || String(a[0]).localeCompare(String(b[0])));
  const inner = Math.max(1, width - pad - 30);
  const pos = new Map();
  const lanes = order.map(([sessionId, ns], li) => {
    const y = top + li * laneH;
    const t0 = tsOf(ns[0].ts);
    const t1 = tsOf(ns[ns.length - 1].ts);
    ns.forEach((n, i) => {
      // Real time when the lane spans any, index when it does not — a lane whose
      // nodes share one timestamp would otherwise stack into a single column.
      const f = t1 > t0 ? (tsOf(n.ts) - t0) / (t1 - t0) : (ns.length > 1 ? i / (ns.length - 1) : 0);
      pos.set(n.id, { x: pad + 20 + f * inner, y });
    });
    return {
      sessionId, y, count: ns.length, spanMs: Math.max(0, t1 - t0),
      nodes: ns.map((n) => ({ ...n, ...pos.get(n.id), linked: linked.has(n.id), family: familyOf(n.kind) })),
    };
  });
  const arcs = [];
  for (const e of built?.edges || []) {
    const a = pos.get(e.source.id); const b = pos.get(e.target.id);
    if (!a || !b || a.y !== b.y) continue;
    const h = Math.min(maxArc, 8 + Math.abs(b.x - a.x) * 0.1);
    arcs.push({
      id: `${e.source.id}→${e.target.id}:${e.tier}`, tier: e.tier, cls: e.class,
      d: `M${a.x},${a.y} C${a.x},${a.y - h} ${b.x},${b.y - h} ${b.x},${b.y}`, edge: e,
    });
  }
  return { width, height: top + order.length * laneH + 26, lanes, arcs };
}

// ── 4. MATRIX ────────────────────────────────────────────────────────────────

/**
 * Family × family, one grid per tier. The densest read of a sparse graph, and
 * the only one where an EMPTY cell carries as much as a full one: a tier that
 * never fires is a blank grid rather than a statistic.
 */
export function matrixLayout(built, { cell = 30 } = {}) {
  const nodes = orderedNodes(built);
  const fams = [...new Set(nodes.map((n) => familyOf(n.kind)))].sort();
  const grids = TIERS.map((tier) => {
    const cells = [];
    let max = 0;
    for (const tf of fams) {
      for (const sf of fams) {
        const n = (built?.edges || []).filter((e) => e.tier === tier && familyOf(e.source.kind) === sf && familyOf(e.target.kind) === tf).length;
        if (n > max) max = n;
        cells.push({ sf, tf, n, row: fams.indexOf(tf), col: fams.indexOf(sf) });
      }
    }
    return { tier, cells, max, total: cells.reduce((s, c) => s + c.n, 0) };
  });
  return { fams, grids, cell, size: fams.length * cell };
}

// ── 5. DEPTH ─────────────────────────────────────────────────────────────────

/**
 * Columns are distance BACK from something produced, not source-versus-target.
 *
 * Everything landing in column 1 means the window holds no chains — which is
 * what the corpus actually showed. The bipartite view degenerates to two columns
 * unconditionally, so it can never report that; here the degeneration IS the
 * reading.
 */
export function depthLayout(built, { colW = 250, rowH = 22, top = 46, pad = 40, maxDepth = 6 } = {}) {
  const edges = built?.edges || [];
  const targets = new Set(edges.map((e) => e.target.id));
  const sourcesOf = new Map();
  for (const e of edges) {
    if (!sourcesOf.has(e.target.id)) sourcesOf.set(e.target.id, []);
    sourcesOf.get(e.target.id).push(e.source.id);
  }
  // Depth 0 is anything produced; a source is one deeper than the shallowest
  // thing it feeds. BFS from the productions, so a node feeding both a depth-0
  // and a depth-3 target lands at 1 — the shortest path is the honest distance.
  const depth = new Map();
  let frontier = [...targets];
  for (const id of frontier) depth.set(id, 0);
  for (let d = 0; d < maxDepth; d++) {
    const next = [];
    for (const id of frontier) {
      for (const s of sourcesOf.get(id) || []) {
        if (depth.has(s)) continue;
        depth.set(s, d + 1);
        next.push(s);
      }
    }
    if (!next.length) break;
    frontier = next;
  }
  const nodeById = new Map(orderedNodes(built).map((n) => [n.id, n]));
  const cols = new Map();
  for (const [id, d] of depth) {
    if (!cols.has(d)) cols.set(d, []);
    cols.get(d).push(nodeById.get(id) || { id, kind: "other", label: id });
  }
  const depths = [...cols.keys()].sort((a, b) => b - a); // deepest left, produced right
  const pos = new Map();
  const columns = depths.map((d, ci) => {
    const x = pad + ci * colW;
    const ns = cols.get(d).sort((a, b) => String(a.label).localeCompare(String(b.label)));
    ns.forEach((n, i) => pos.set(n.id, { x, y: top + i * rowH }));
    return {
      depth: d, x, count: ns.length,
      label: d === 0 ? "produced" : `${d} step${d > 1 ? "s" : ""} back`,
      nodes: ns.map((n) => ({ ...n, ...pos.get(n.id), family: familyOf(n.kind) })),
    };
  });
  const links = [];
  for (const e of edges) {
    const a = pos.get(e.source.id); const b = pos.get(e.target.id);
    if (!a || !b) continue;
    links.push({
      id: `${e.source.id}→${e.target.id}:${e.tier}`, tier: e.tier, cls: e.class,
      d: `M${a.x + 4},${a.y} C${a.x + colW * 0.4},${a.y} ${b.x - colW * 0.35},${b.y} ${b.x + 4},${b.y}`,
    });
  }
  const unlinkedNodes = orderedNodes(built).filter((n) => !depth.has(n.id));
  const tallest = Math.max(1, ...columns.map((c) => c.count), unlinkedNodes.length);
  return {
    columns, links, unlinkedNodes,
    width: pad + Math.max(1, columns.length) * colW + 180,
    height: top + tallest * rowH + 20,
  };
}
